import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabaseClient';
import { generateQRCode, downloadQRCode } from '../../lib/qrCodeGenerator';
import { 
  Save, 
  Plus, 
  Trash, 
  ArrowUp, 
  ArrowDown, 
  Copy, 
  Loader,
  QrCode,
  Download,
  Check
} from 'lucide-react';

// TypeScript interfaces
interface Question {
  id: string;
  type: string;
  label: string;
  required: boolean;
  options: string[];
}

interface FormData {
  name: string;
  description: string;
  is_active: boolean;
  thank_you_message: string;
  notification_email: string;
  email_notifications_enabled: boolean;
  questions: Question[];
}

// Question type components
const QuestionTypes = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
  SELECT: 'select',
  RATING: 'rating',
};

const FormBuilderPage = () => {
  const { formId } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    is_active: true,
    thank_you_message: 'Thank you for your feedback! We will review your feedback and act on it. If you left contact information, we will get back to you.',
    notification_email: '',
    email_notifications_enabled: false,
    questions: [],
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showQrPreview, setShowQrPreview] = useState(false);
  
  // Load form data if editing
  useEffect(() => {
    if (formId) {
      fetchFormData();
    } else {
      // Default new form with one question
      setFormData({
        ...formData,
        questions: [
          {
            id: 'q1',
            type: QuestionTypes.TEXT,
            label: 'What do you think about our service?',
            required: true,
            options: [],
          },
        ],
      });
      setLoading(false);
    }
  }, [formId]);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('forms')
        .select('*')
        .eq('id', formId)
        .single();

      if (error) throw error;
      
      if (data.user_id !== user?.id) {
        addToast('You do not have permission to edit this form', 'error');
        navigate('/dashboard/forms');
        return;
      }
      
      setFormData(data);
      generateFormQrCode(data.id);
    } catch (error) {addToast('Failed to load form data', 'error');
      navigate('/dashboard/forms');
    } finally {
      setLoading(false);
    }
  };

  const generateFormQrCode = async (id: string) => {
    try {
      const url = `${window.location.origin}/feedback/${id}`;
      const qrCode = await generateQRCode(url);
      setQrCodeUrl(qrCode);
    } catch (error) {}
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleQuestionChange = (index: number, field: keyof Question, value: any) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value,
    };
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const handleOptionChange = (questionIndex: number, optionIndex: number, value: string) => {
    const updatedQuestions = [...formData.questions];
    const updatedOptions = [...updatedQuestions[questionIndex].options];
    updatedOptions[optionIndex] = value;
    updatedQuestions[questionIndex].options = updatedOptions;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q${formData.questions.length + 1}`,
      type: QuestionTypes.TEXT,
      label: '',
      required: false,
      options: [],
    };
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion],
    });
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions.splice(index, 1);
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === formData.questions.length - 1)
    ) {
      return;
    }

    const updatedQuestions = [...formData.questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    [updatedQuestions[index], updatedQuestions[newIndex]] = 
      [updatedQuestions[newIndex], updatedQuestions[index]];
    
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const addOption = (questionIndex: number) => {
    const updatedQuestions = [...formData.questions];
    const updatedOptions = [
      ...updatedQuestions[questionIndex].options,
      `Option ${updatedQuestions[questionIndex].options.length + 1}`,
    ];
    updatedQuestions[questionIndex].options = updatedOptions;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const updatedQuestions = [...formData.questions];
    const updatedOptions = [...updatedQuestions[questionIndex].options];
    updatedOptions.splice(optionIndex, 1);
    updatedQuestions[questionIndex].options = updatedOptions;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      addToast('Please enter a form name', 'error');
      return false;
    }

    for (let i = 0; i < formData.questions.length; i++) {
      const question = formData.questions[i];
      if (!question.label.trim()) {
        addToast(`Question ${i + 1} needs a label`, 'error');
        return false;
      }

      if (
        ['radio', 'checkbox', 'select'].includes(question.type) &&
        question.options.length < 2
      ) {
        addToast(`Question ${i + 1} needs at least 2 options`, 'error');
        return false;
      }
    }

    return true;
  };

  const saveForm = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      
      const formPayload = {
        ...formData,
        user_id: user?.id,
      };
      
      if (formId) {
        // Update existing form
        const { error } = await supabase
          .from('forms')
          .update(formPayload)
          .eq('id', formId);

        if (error) throw error;
        
        addToast('Form updated successfully', 'success');
      } else {
        // Create new form
        const { data, error } = await supabase
          .from('forms')
          .insert([formPayload])
          .select()
          .single();

        if (error) throw error;
        
        // Generate QR code for the new form
        await generateFormQrCode(data.id);
        
        addToast('Form created successfully', 'success');
        navigate(`/dashboard/forms/${data.id}`);
      }
    } catch (error) {addToast('Failed to save form', 'error');
    } finally {
      setSaving(false);
    }
  };

  const copyFormLink = () => {
    const url = `${window.location.origin}/feedback/${formId}`;
    navigator.clipboard.writeText(url);
    addToast('Form link copied to clipboard', 'success');
  };

  const downloadQr = () => {
    const fileName = `qr-form-${formData.name.replace(/\s+/g, '-').toLowerCase()}`;
    downloadQRCode(qrCodeUrl, fileName);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader size={40} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {formId ? 'Edit Form' : 'Create New Form'}
          </h1>
          <p className="text-sm text-gray-500">
            {formId ? 'Update your feedback form' : 'Create a new feedback form for your customers'}
          </p>
        </div>

        <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
          {formId && (
            <>
              <button
                type="button"
                onClick={() => setShowQrPreview(!showQrPreview)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <QrCode size={16} className="mr-2" />
                {showQrPreview ? 'Hide' : 'Show'} QR
              </button>
              
              <button
                type="button"
                onClick={copyFormLink}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Copy size={16} className="mr-2" />
                Copy Link
              </button>
            </>
          )}
          
          <button
            type="button"
            onClick={saveForm}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Form
              </>
            )}
          </button>
        </div>
      </div>

      {/* QR Preview */}
      {formId && showQrPreview && (
        <div className="mb-6 p-4 bg-white shadow rounded-lg">
          <div className="flex flex-col sm:flex-row items-center">
            <div className="mb-4 sm:mb-0 sm:mr-6 bg-white p-2 rounded-lg border border-gray-200">
              <img src={qrCodeUrl} alt="QR Code" className="w-32 h-32" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Form QR Code
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Share this QR code with your customers to collect feedback
              </p>
              <button
                type="button"
                onClick={downloadQr}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download size={16} className="mr-2" />
                Download QR Code
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        {/* Form Details */}
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Form Details</h2>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Form Name *
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleFormChange}
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                placeholder="Customer Feedback"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                id="description"
                rows={3}
                value={formData.description}
                onChange={handleFormChange}
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                placeholder="Please share your thoughts about our service..."
              />
            </div>

            <div>
              <label htmlFor="thank_you_message" className="block text-sm font-medium text-gray-700">
                Thank You Message
              </label>
              <textarea
                name="thank_you_message"
                id="thank_you_message"
                rows={2}
                value={formData.thank_you_message}
                onChange={handleFormChange}
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                placeholder="Thank you for your feedback! We will review your feedback and act on it. If you left contact information, we will get back to you."
              />
            </div>

            <div>
              <label htmlFor="notification_email" className="block text-sm font-medium text-gray-700">
                Notification Email
              </label>
              <input
                type="email"
                name="notification_email"
                id="notification_email"
                value={formData.notification_email}
                onChange={handleFormChange}
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                placeholder="notifications@example.com"
              />
              <p className="mt-1 text-sm text-gray-500">
                Receive email notifications when someone submits this form
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="email_notifications_enabled"
                id="email_notifications_enabled"
                checked={formData.email_notifications_enabled}
                onChange={handleFormChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="email_notifications_enabled" className="ml-2 block text-sm text-gray-700">
                Enable email notifications for this form
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                id="is_active"
                checked={formData.is_active}
                onChange={handleFormChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                Form is active and accepting responses
              </label>
            </div>
          </div>
        </div>

        {/* Form Questions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Questions</h2>
            <button
              type="button"
              onClick={addQuestion}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus size={16} className="mr-1" />
              Add Question
            </button>
          </div>

          {formData.questions.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900">No questions yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first question
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={addQuestion}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus size={16} className="mr-2" />
                  Add Question
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {formData.questions.map((question, index) => (
                <div key={question.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between mb-4">
                    <h3 className="text-md font-medium text-gray-900">
                      Question {index + 1}
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => moveQuestion(index, 'up')}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-500 disabled:opacity-30"
                        title="Move up"
                      >
                        <ArrowUp size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveQuestion(index, 'down')}
                        disabled={index === formData.questions.length - 1}
                        className="text-gray-400 hover:text-gray-500 disabled:opacity-30"
                        title="Move down"
                      >
                        <ArrowDown size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeQuestion(index)}
                        className="text-red-400 hover:text-red-500"
                        title="Remove question"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor={`question-${index}-label`} className="block text-sm font-medium text-gray-700">
                        Question Text *
                      </label>
                      <input
                        type="text"
                        id={`question-${index}-label`}
                        value={question.label}
                        onChange={(e) => handleQuestionChange(index, 'label', e.target.value)}
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        placeholder="Enter question text"
                      />
                    </div>

                    <div>
                      <label htmlFor={`question-${index}-type`} className="block text-sm font-medium text-gray-700">
                        Question Type
                      </label>
                      <select
                        id={`question-${index}-type`}
                        value={question.type}
                        onChange={(e) => handleQuestionChange(index, 'type', e.target.value)}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value={QuestionTypes.TEXT}>Short Answer</option>
                        <option value={QuestionTypes.TEXTAREA}>Long Answer</option>
                        <option value={QuestionTypes.RADIO}>Multiple Choice (Single Select)</option>
                        <option value={QuestionTypes.CHECKBOX}>Multiple Choice (Multi Select)</option>
                        <option value={QuestionTypes.SELECT}>Dropdown</option>
                        <option value={QuestionTypes.RATING}>Rating</option>
                      </select>
                    </div>

                    {/* Options for multiple choice questions */}
                    {['radio', 'checkbox', 'select'].includes(question.type) && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Options
                          </label>
                          <button
                            type="button"
                            onClick={() => addOption(index)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200"
                          >
                            <Plus size={14} className="mr-1" />
                            Add Option
                          </button>
                        </div>
                        
                        {question.options.length === 0 ? (
                          <div className="text-center py-4 border border-dashed border-gray-300 rounded">
                            <p className="text-xs text-gray-500">
                              No options yet. Add at least 2 options.
                            </p>
                          </div>
                        ) : (
                          <ul className="space-y-2">
                            {question.options.map((option, optionIndex) => (
                              <li key={optionIndex} className="flex items-center">
                                <input
                                  type="text"
                                  value={option}
                                  onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                                  className="block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                  placeholder={`Option ${optionIndex + 1}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeOption(index, optionIndex)}
                                  className="ml-2 text-red-400 hover:text-red-500"
                                >
                                  <Trash size={16} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`question-${index}-required`}
                        checked={question.required}
                        onChange={(e) => handleQuestionChange(index, 'required', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`question-${index}-required`} className="ml-2 block text-sm text-gray-700">
                        This question is required
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={saveForm}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Form
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormBuilderPage;